"use strict";
/**
 * Shared helper functions
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidOption = isValidOption;
exports.isPnta = isPnta;
exports.filterPnta = filterPnta;
exports.getZodiacFromBirthDate = getZodiacFromBirthDate;
exports.genderIdToInterestedIn = genderIdToInterestedIn;
exports.interestedInToGenderIds = interestedInToGenderIds;
/**
 * Check if a value is valid for a given option array
 */
function isValidOption(arr, value) {
    return arr.includes(value);
}
/**
 * Check if a value represents "prefer not to answer"
 * Returns true for 'pnta', '[pnta]', or ['pnta']
 */
function isPnta(value) {
    if (!value)
        return false;
    if (value === 'pnta' || value === '[pnta]')
        return true;
    if (Array.isArray(value) && value.length === 1 && value[0] === 'pnta')
        return true;
    return false;
}
/**
 * Filter out PNTA values for display purposes
 * Returns null if the value is PNTA, otherwise returns the value unchanged
 */
function filterPnta(value) {
    if (isPnta(value))
        return null;
    return value;
}
/**
 * Get zodiac sign from birth date
 * @param birthDate Date string in YYYY-MM-DD format or Date object
 * @returns Zodiac sign string or null if invalid date
 */
function getZodiacFromBirthDate(birthDate) {
    const date = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
    if (isNaN(date.getTime()))
        return null;
    const month = date.getMonth() + 1;
    const day = date.getDate();
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19))
        return 'aries';
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20))
        return 'taurus';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20))
        return 'gemini';
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22))
        return 'cancer';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22))
        return 'leo';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22))
        return 'virgo';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22))
        return 'libra';
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21))
        return 'scorpio';
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21))
        return 'sagittarius';
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19))
        return 'capricorn';
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18))
        return 'aquarius';
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20))
        return 'pisces';
    return null;
}
/**
 * Map a gender_id to the corresponding InterestedInGender value.
 * Used for two-way matching and fake user generation.
 *   1 = Male   -> 'men'
 *   2 = Female -> 'women'
 *   3-8        -> 'nonbinary'
 *   9+ / other -> null (skip check)
 */
function genderIdToInterestedIn(genderId) {
    if (genderId === 1)
        return 'men';
    if (genderId === 2)
        return 'women';
    if (genderId >= 3 && genderId <= 8)
        return 'nonbinary';
    return null;
}
/**
 * Map InterestedInGender values to the corresponding gender_ids.
 * Reverse of genderIdToInterestedIn — used by the client to build search params.
 * Always includes gender_id 9 (prefer not to say) when any ids are matched.
 */
const INTERESTED_IN_TO_GENDER_IDS = {
    men: [1],
    women: [2],
    nonbinary: [3, 4, 5, 6, 7, 8],
};
const PREFER_NOT_TO_SAY_GENDER_ID = 9;
function interestedInToGenderIds(interested) {
    const ids = new Set();
    for (const val of interested) {
        const mapped = INTERESTED_IN_TO_GENDER_IDS[val];
        if (mapped)
            mapped.forEach(id => ids.add(id));
    }
    if (ids.size > 0)
        ids.add(PREFER_NOT_TO_SAY_GENDER_ID);
    return Array.from(ids);
}
